import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PanelFrame from "../../ui/PanelFrame";
import siteConfig from "../../../config/siteConfig";
import { useLang } from "../../../i18n/LangContext";
import { guidesHe } from "../../../i18n/translations";
import guidesParagraphsHe from "../../../i18n/guidesParagraphsHe";
import "./Guides.scss";

function guideCatTitle(cat, isHe) {
  if (!isHe) return cat.title;
  return guidesHe[cat.id]?.title || cat.title;
}

function guideCatSubtitle(cat, isHe) {
  if (!isHe) return cat.subtitle;
  return guidesHe[cat.id]?.subtitle || cat.subtitle;
}

function guideTopicTitle(topic, isHe) {
  if (!isHe) return topic.title;
  return guidesHe[topic.id] || topic.title;
}

function guideParagraphs(topic, isHe) {
  if (!isHe) return topic.paragraphs || [];
  return guidesParagraphsHe[topic.id] || topic.paragraphs || [];
}

const Guides = () => {
  const { t, isHe } = useLang();
  const categories = siteConfig.guides || [];
  const [searchParams] = useSearchParams();

  const firstTopic = useMemo(() => {
    const cat = categories[0];
    const topic = cat?.topics?.[0];
    if (!cat || !topic) return null;
    return { categoryId: cat.id, topicId: topic.id };
  }, [categories]);

  const deepLink = useMemo(() => {
    const catId = searchParams.get("cat");
    const topicId = searchParams.get("topic");
    if (!catId || !topicId) return null;
    const cat = categories.find((c) => c.id === catId);
    const topic = cat?.topics?.find((t) => t.id === topicId);
    if (!cat || !topic) return null;
    return { categoryId: cat.id, topicId: topic.id };
  }, [categories, searchParams]);

  const [openCategoryId, setOpenCategoryId] = useState(
    deepLink?.categoryId || firstTopic?.categoryId || null
  );
  const [active, setActive] = useState(deepLink || firstTopic);

  useEffect(() => {
    if (deepLink) {
      setActive(deepLink);
      setOpenCategoryId(deepLink.categoryId);
      return;
    }
    if (!active && firstTopic) {
      setActive(firstTopic);
      setOpenCategoryId(firstTopic.categoryId);
    }
  }, [deepLink, active, firstTopic]);

  const activeCategory = categories.find((c) => c.id === active?.categoryId);
  const activeTopic = activeCategory?.topics?.find(
    (topic) => topic.id === active?.topicId
  );

  const toggleCategory = (id) => {
    setOpenCategoryId((prev) => (prev === id ? null : id));
  };

  const selectTopic = (categoryId, topicId) => {
    setOpenCategoryId(categoryId);
    setActive({ categoryId, topicId });
  };

  return (
    <div className="mu-guides">
      <div className="mu-guides__toolbar">
        <Link className="mu-guides__back" to="/">
          {t("back_to_home")}
        </Link>
      </div>

      <div className="mu-guides__layout">
        <aside className="mu-guides__nav">
          <PanelFrame title={t("guides")} className="mu-guides__nav-frame">
            <ul className="mu-guides__categories">
              {categories.map((cat) => {
                const isOpen = openCategoryId === cat.id;
                return (
                  <li key={cat.id} className="mu-guides__category">
                    <button
                      type="button"
                      className={`mu-guides__cat-btn${
                        isOpen ? " is-open" : ""
                      }${
                        active?.categoryId === cat.id ? " is-active-cat" : ""
                      }`}
                      onClick={() => toggleCategory(cat.id)}
                      aria-expanded={isOpen}
                    >
                      <span className="mu-guides__cat-text">
                        <strong>{guideCatTitle(cat, isHe)}</strong>
                        <span>{guideCatSubtitle(cat, isHe)}</span>
                      </span>
                      <span className="mu-guides__chevron" aria-hidden>
                        {isOpen ? "▾" : "▸"}
                      </span>
                    </button>

                    {isOpen ? (
                      <ul className="mu-guides__topics">
                        {(cat.topics || []).map((topic) => (
                          <li key={topic.id}>
                            <button
                              type="button"
                              className={`mu-guides__topic-btn${
                                active?.topicId === topic.id &&
                                active?.categoryId === cat.id
                                  ? " is-active"
                                  : ""
                              }`}
                              onClick={() => selectTopic(cat.id, topic.id)}
                            >
                              {guideTopicTitle(topic, isHe)}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </PanelFrame>
        </aside>

        <section className="mu-guides__content">
          <PanelFrame
            title={
              activeTopic
                ? guideTopicTitle(activeTopic, isHe)
                : t("select_a_guide")
            }
            className="mu-guides__content-frame"
          >
            {activeTopic ? (
              <div className="mu-guides__article">
                <p className="mu-guides__breadcrumb">
                  {guideCatTitle(activeCategory, isHe)}
                  {" / "}
                  {guideTopicTitle(activeTopic, isHe)}
                </p>
                {(guideParagraphs(activeTopic, isHe) || []).map((p) => (
                  <p key={p}>{p}</p>
                ))}
                {Array.isArray(activeTopic.items) && activeTopic.items.length ? (
                  <div className="mu-guides__items">
                    {activeTopic.items.map((item) => (
                      <figure key={item.label} className="mu-guides__item">
                        <img src={item.src} alt={item.label} />
                        <figcaption>{item.label}</figcaption>
                      </figure>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="mu-guides__empty">{t("choose_guide_topic")}</p>
            )}
          </PanelFrame>
        </section>
      </div>
    </div>
  );
};

export default Guides;
